import type { ColumnDef } from "@tanstack/react-table";

enum TableColumnFilterOperator {
  Contains = "contains",
  DoesNotContain = "does not contain",
  Equals = "equals",
  DoesNotEqual = "does not equal",
  StartsWith = "starts with",
  EndsWith = "ends with",
  IsEmpty = "is empty",
  IsNotEmpty = "is not empty",
}

interface TableColumnFilter {
  columnId: string;
  operator: TableColumnFilterOperator;
  text: string;
}

function getColumnDefValue<T>(columnDef: ColumnDef<T, any>, row: T): unknown {
  if ("accessorFn" in columnDef && columnDef.accessorFn) {
    return columnDef.accessorFn(row, 0);
  }

  if ("accessorKey" in columnDef && columnDef.accessorKey) {
    return String(columnDef.accessorKey)
      .split(".")
      .reduce<unknown>((value, key) => (value as Record<string, unknown> | undefined)?.[key], row);
  }

  return undefined;
}

const tableColumnTextFilterFn = <T extends object>(
  columns: ColumnDef<T, any>[],
  row: T,
  columnFilters: TableColumnFilter[],
): boolean => {
  let isMatch = true;

  columnFilters.forEach((columnFilter) => {
    const columnDef = columns.find((column) => column.id === columnFilter.columnId);
    const columnText = String((columnDef && getColumnDefValue(columnDef, row)) ?? "").toLowerCase();
    const filterText = columnFilter.text.toLowerCase();

    switch (columnFilter.operator) {
      case TableColumnFilterOperator.Contains:
        isMatch = isMatch && columnText.includes(filterText);
        break;
      case TableColumnFilterOperator.DoesNotContain:
        isMatch = isMatch && !columnText.includes(filterText);
        break;
      case TableColumnFilterOperator.Equals:
        isMatch = isMatch && columnText === filterText;
        break;
      case TableColumnFilterOperator.DoesNotEqual:
        isMatch = isMatch && columnText !== filterText;
        break;
      case TableColumnFilterOperator.StartsWith:
        isMatch = isMatch && columnText.startsWith(filterText);
        break;
      case TableColumnFilterOperator.EndsWith:
        isMatch = isMatch && columnText.endsWith(filterText);
        break;
      case TableColumnFilterOperator.IsEmpty:
        isMatch = isMatch && columnText === "";
        break;
      case TableColumnFilterOperator.IsNotEmpty:
        isMatch = isMatch && columnText !== "";
        break;
    }
  });

  return isMatch;
};

export { TableColumnFilter, TableColumnFilterOperator, tableColumnTextFilterFn };
